"use client";

import { useState } from "react";
import { Download, Plus, X, TriangleAlert as AlertTriangle } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function CommonInquiries() {
  const [openItem, setOpenItem] = useState<string>("item-3");
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: bannerRef,
    isVisible: bannerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const instructions = [{
    id: "item-1",
    partNumber: "§ 01",
    title: "STRATEGY AUDIT",
    time: "2 MIN",
    tools: "DATA SUITE",
    frequency: "DAILY",
    steps: [{
      label: "STEP .01",
      text: "Analyze current traffic patterns to identify friction points. Never deploy capital without a baseline performance metric."
    }, {
      label: "STEP .02",
      text: "Apply proprietary growth algorithms to each channel. Distribute budget with precision in high-conversion segments."
    }, {
      label: "STEP .03",
      text: "Review real-time analytics and adjust bidding strategies. Store all campaign data in the secure cloud vault."
    }],
    warning: "DO NOT IGNORE ANOMALIES"
  }, {
    id: "item-2",
    partNumber: "§ 02",
    title: "ADJUSTING BID CAPS",
    time: "3 MIN",
    tools: "AUTO-SCALER",
    frequency: "AS NEEDED",
    steps: [{
      label: "STEP .01",
      text: "Access the campaign dashboard with administrative credentials. Locate the bid management module."
    }, {
      label: "STEP .02",
      text: "Apply incremental adjustments to maximize ROAS. Never exceed the pre-defined risk threshold per click."
    }, {
      label: "STEP .03",
      text: "Verify the new bid cap against current market volatility. The campaign should maintain steady velocity."
    }],
    warning: "BID CAPS ARE VOLATILITY-SENSITIVE"
  }, {
    id: "item-3",
    partNumber: "§ 03",
    title: "OPTIMIZING CONVERSIONS",
    time: "4 MIN",
    tools: "A/B ENGINE",
    frequency: "MONTHLY",
    steps: [{
      label: "STEP .01",
      text: "Isolate the landing page variant with the lowest engagement. Identify the primary conversion bottleneck."
    }, {
      label: "STEP .02",
      text: "Insert the new creative assets and trigger the A/B test. Stop when statistical significance is reached."
    }, {
      label: "STEP .03",
      text: "Deploy the winning variant to the live environment. Repeat for the secondary funnel."
    }],
    warning: "OVER-OPTIMIZATION DILUTES BRAND"
  }, {
    id: "item-4",
    partNumber: "§ 04",
    title: "RESHAPING AUDIENCES",
    time: "6 MIN",
    tools: "PIXEL DATA",
    frequency: "QUARTERLY",
    steps: [{
      label: "STEP .01",
      text: "Submerge the audience segments in high-intent data pools for ninety seconds."
    }, {
      label: "STEP .02",
      text: "Remove and refine targeting parameters. Audience profiles become pliable but cool within thirty seconds."
    }, {
      label: "STEP .03",
      text: "Hold the new audience configuration under cold analytical review for one minute to set."
    }],
    warning: "DO NOT EXCEED TARGETING LIMITS"
  }, {
    id: "item-5",
    partNumber: "§ 05",
    title: "STORING CAMPAIGN ASSETS",
    time: "30 SEC",
    tools: "VAULT + KEY",
    frequency: "DAILY",
    steps: [{
      label: "STEP .01",
      text: "Archive the campaign assets in their natural folder structure. Primary assets first, then secondary."
    }, {
      label: "STEP .02",
      text: "Encrypt the campaign data before placing it into the secure storage vault."
    }, {
      label: "STEP .03",
      text: "Close the vault fully. Store away from direct public access and unauthorized heat sources."
    }],
    warning: "AVOID PUBLIC REPOSITORIES"
  }, {
    id: "item-6",
    partNumber: "§ 06",
    title: "REMOVING PERFORMANCE LAG",
    time: "N/A",
    tools: "LAB ONLY",
    frequency: "RETURN FOR SERVICE",
    steps: [{
      label: "STEP .01",
      text: "Do not attempt at-home performance fixes. Consumer optimization tools degrade brand equity."
    }, {
      label: "STEP .02",
      text: "Document the performance drop with a screenshot and contact the lab directly."
    }, {
      label: "STEP .03",
      text: "Ship the campaign files in the original secure container using the prepaid maintenance label."
    }],
    warning: "DIY METHODS VOID THE WARRANTY"
  }, {
    id: "item-7",
    partNumber: "§ 07",
    title: "PROTECTING BRAND EQUITY",
    time: "ONGOING",
    tools: "AWARENESS",
    frequency: "ALWAYS",
    steps: [{
      label: "STEP .01",
      text: "Avoid contact with low-quality traffic, spam bots, and household marketing solvents. These strip the brand layer within seconds."
    }, {
      label: "STEP .02",
      text: "Remove brand assets before applying aggressive discount campaigns. Reapply only when market conditions have stabilized."
    }, {
      label: "STEP .03",
      text: "Rinse immediately if exposed to negative PR, viral backlash, or industrial market shifts."
    }],
    warning: "BRAND DAMAGE IS NOT COVERED"
  }, {
    id: "item-8",
    partNumber: "§ 08",
    title: "ANNUAL LAB INSPECTION",
    time: "30 MIN",
    tools: "BY APPOINTMENT",
    frequency: "YEARLY",
    steps: [{
      label: "STEP .01",
      text: "Schedule an inspection appointment via the lab contact line or the online booking system."
    }, {
      label: "STEP .02",
      text: "Bring all owned Catalyst Growth Lab campaign assets and their original documentation to the lab."
    }, {
      label: "STEP .03",
      text: "Technicians will realign, retighten, and re-polish each campaign. Wait time is approximately thirty minutes per pair."
    }],
    warning: "BOOK SIX WEEKS IN ADVANCE"
  }];
  return <section data-section-id="1832" id="common-inquiries" className="bg-background text-foreground py-20 md:py-28 lg:py-44 overflow-hidden">
      <div ref={bannerRef} className={`relative w-full aspect-[16/4] mb-16 md:mb-24 overflow-hidden border-y border-border transition-all duration-700 ${bannerVisible ? "opacity-100" : "opacity-0"}`}>
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701027/img/catalyst_lab_banner.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701027/img/catalyst_lab_banner.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701027/img/catalyst_lab_banner.webp-wvc-sizes" alt="Top-down view of a Catalyst Growth Lab strategy dashboard" loading="lazy" decoding="async" className="w-full h-full object-cover" />
        <div className="absolute top-4 left-4 md:top-6 md:left-6 font-mono text-[10px] md:text-xs tracking-[0.18em] text-background bg-primary px-2 py-1">
          + GROWTH-MANUAL-01
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-4" style={{
        backgroundImage: "repeating-linear-gradient(45deg, var(--primary), var(--primary) 8px, var(--background) 8px, var(--background) 16px)"
      }} aria-hidden="true" />
      </div>

      <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
          <div ref={headerRef} className="lg:col-span-4">
            <div className={`lg:sticky lg:top-24 relative transition-all duration-700 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              <div aria-hidden="true" className="pointer-events-none absolute -bottom-4 -left-4 font-serif font-bold leading-none select-none opacity-[0.08]" style={{
              fontSize: "clamp(8rem, 14vw, 16rem)"
            }}>
                10
              </div>

              <div className="flex items-center gap-3 mb-6 relative">
                <span className="block w-8 h-[3px] bg-primary" aria-hidden="true" />
                <span className="font-mono text-xs tracking-[0.18em] text-primary">
                  PN-10 
                </span>
              </div>

              <h2 className="font-default font-bold leading-[0.92] tracking-tight text-foreground mb-10 relative" style={{
              fontSize: "clamp(2.5rem, 5.5vw, 5rem)"
            }}>
                SCALE /<br />
                YOUR /<br />
                <span className="relative inline-block">
                  IMPACT.
                  <span aria-hidden="true" className="absolute left-0 right-0 -bottom-2 h-3" style={{
                  backgroundImage: "repeating-linear-gradient(45deg, var(--primary), var(--primary) 6px, var(--background) 6px, var(--background) 12px)"
                }} />
                </span>
              </h2>

              <div className="relative">
                <div className="font-mono text-[10px] tracking-[0.18em] text-muted-foreground pb-3 border-b border-border mb-3">
                  CONTENTS .... 08 ITEMS
                </div>
                <ul className="space-y-2.5">
                  {instructions.map((item, i) => {
                  const isActive = openItem === item.id;
                  return <li key={item.id} data-index={i}>
                        <Button onClick={() => setOpenItem(item.id)} variant="ghost" className="flex items-center gap-3 w-full h-auto p-0 justify-start hover:bg-transparent">
                          <span className={`block w-2 h-2 shrink-0 transition-colors duration-200 ${isActive ? "bg-primary" : "bg-border"}`} aria-hidden="true" />
                          <span className={`font-mono text-[11px] tracking-[0.14em] transition-colors duration-200 ${isActive ? "text-foreground" : "text-muted-foreground hover:text-foreground"}`}>
                            {item.partNumber} &nbsp; {item.title}
                          </span>
                        </Button>
                      </li>;
                })}
                </ul>
              </div>
            </div>
          </div>

          <div className="lg:col-span-8">
            <Accordion type="single" collapsible value={openItem} onValueChange={v => setOpenItem(v)} className="border-t border-border">
              {instructions.map((item, i) => <AccordionItem key={item.id} value={item.id} data-index={i} className="border-b border-border">
                  <AccordionTrigger className="hover:no-underline py-7 [&>svg]:hidden group">
                    <div className="flex items-center gap-4 md:gap-6 w-full pr-4">
                      <span className="font-mono text-xs md:text-sm tracking-[0.14em] text-primary shrink-0 w-12 md:w-16">
                        {item.partNumber}
                      </span>
                      <span className="font-default font-semibold text-foreground text-left leading-none flex-1" style={{
                    fontSize: "clamp(1.5rem, 2.4vw, 2.25rem)"
                  }}>
                        {item.title}
                      </span>
                      <span className="shrink-0 w-6 h-6 flex items-center justify-center text-foreground transition-transform duration-300 group-data-[state=open]:rotate-45">
                        {openItem === item.id ? <X className="w-5 h-5" strokeWidth={1.5} /> : <Plus className="w-5 h-5" strokeWidth={1.5} />}
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pb-10">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-8 pt-2">
                      <div className="md:col-span-4">
                        <div className="bg-card border border-border p-6 mb-5">
                          <div className="flex items-center justify-center aspect-square mb-4 border border-border">
                            <div className="relative w-24 h-24">
                              <svg viewBox="0 0 96 96" className="w-full h-full text-primary" fill="none" strokeWidth="1.5" stroke="currentColor" strokeLinecap="square" strokeLinejoin="miter">
                                <circle cx="48" cy="48" r="36" />
                                <path d="M48 12v36l24 24" />
                              </svg>
                              <span className="absolute -top-2 -left-2 font-mono text-[10px] tracking-[0.14em] text-primary">
                                +
                              </span>
                            </div>
                          </div>
                          <div className="font-mono text-[10px] tracking-[0.14em] text-muted-foreground text-center">
                            FIG. {String(i + 1).padStart(2, "0")}
                          </div>
                        </div>

                        <div className="border border-border p-5 space-y-2.5">
                          {[{
                        label: "TIME",
                        value: item.time
                      }, {
                        label: "TOOLS",
                        value: item.tools
                      }, {
                        label: "FREQUENCY",
                        value: item.frequency
                      }].map((spec, sIdx) => <div key={spec.label} data-index={sIdx} className="flex items-baseline gap-2 font-mono text-[11px] tracking-[0.12em]">
                              <span className="text-muted-foreground">{spec.label}</span>
                              <span className="flex-1 border-b border-dotted border-border translate-y-[-3px]" aria-hidden="true" />
                              <span className="text-foreground">{spec.value}</span>
                            </div>)}
                        </div>
                      </div>

                      <div className="md:col-span-8">
                        <div className="space-y-7">
                          {item.steps.map((step, sIdx) => <div key={step.label} data-index={sIdx} className="flex gap-5">
                              <span className="font-mono text-[11px] tracking-[0.14em] text-primary shrink-0 w-16 pt-1.5">
                                {step.label}
                              </span>
                              <p className="font-default text-muted-foreground leading-relaxed flex-1" style={{
                          fontSize: "clamp(1rem, 1.15vw, 1.125rem)"
                        }}>
                                {step.text}
                              </p>
                            </div>)}
                        </div>

                        <div className="mt-8 border-2 border-primary p-4 flex items-start gap-3 bg-card">
                          <AlertTriangle className="w-5 h-5 text-primary shrink-0 mt-0.5" strokeWidth={1.5} />
                          <div className="font-mono text-xs tracking-[0.14em] text-foreground">
                            <span className="text-primary">WARNING /</span>{" "}
                            {item.warning}
                          </div>
                        </div>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>

            <div className="mt-16 border border-border p-6 md:p-7 space-y-3">
              {[{
              label: "STEPS DOCUMENTED",
              value: "47"
            }, {
              label: "LAST UPDATED",
              value: "2025.03"
            }, {
              label: "PDF DOWNLOAD",
              value: "AVAILABLE"
            }].map((spec, idx) => <div key={spec.label} data-index={idx} className="flex items-baseline gap-3 font-mono text-xs tracking-[0.14em]">
                  <span className="text-muted-foreground">{spec.label}</span>
                  <span className="flex-1 border-b border-dotted border-border translate-y-[-3px]" aria-hidden="true" />
                  <span className="text-foreground">{spec.value}</span>
                </div>)}
            </div>

            <div className="mt-10">
              <Button asChild variant="outline" className="bg-transparent border-[1.5px] border-primary text-primary hover:bg-primary hover:text-background font-default tracking-[0.08em] uppercase text-base px-7 py-6 h-auto transition-colors duration-200 group">
                <Link to="/ContactPage">
                  <Download className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  DOWNLOAD THE STRATEGY PDF
                  <span className="ml-2 transition-transform duration-200 group-hover:translate-x-1.5">
                    →
                  </span>
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}